import { z } from "zod";
import { budgetHealthSchema } from "./budget-health";
import { metricSnapshotSchema } from "./campaign";
import {
  campaignAnalyticsGranularitySchema,
  campaignAnalyticsKpiSchema,
  campaignAnalyticsTrendPointSchema,
  csvQueryValues,
  dashboardRangeSchema,
} from "./dashboard";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

/**
 * Portfolio read contract (ADP-89). Shop-scoped: the API only ever returns the
 * portfolios of the caller's active shop, so the creation wizard can select one
 * and the dashboard can show portfolio budget health without cross-shop leakage.
 */
export const portfolioStateSchema = z.enum(["enabled", "paused", "archived"]);
export type PortfolioState = z.infer<typeof portfolioStateSchema>;

export const portfolioSummarySchema = z.object({
  id: z.string().uuid(),
  amazonPortfolioId: z.string(),
  name: z.string(),
  state: portfolioStateSchema,
  budgetHealth: budgetHealthSchema,
});
export type PortfolioSummary = z.infer<typeof portfolioSummarySchema>;

export const portfolioListResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(portfolioSummarySchema),
});

// ── Portfolio analytics (ADP-121) ───────────────────────────────────────────
//
// Aggregates campaign metrics by Portfolio. The KPI summary + trend reuse the
// campaign-analytics read model verbatim (fed the portfolio's member campaign
// ids), so their atoms are shared 1:1; only the per-portfolio LIST row and the
// member-campaign row are new here.

/** One row in the portfolio analytics list — a portfolio summary plus its
 * aggregate member metrics over the selected window and its member count. */
export const portfolioListItemSchema = portfolioSummarySchema.extend({
  // ADP-110: disambiguates portfolios across shops in the aggregated list.
  shopId: z.string().default(""),
  shopName: z.string().nullable().default(null),
  memberCount: z.number().int().nonnegative(),
  metrics: metricSnapshotSchema,
  // Raw budget meta backing the LIST-page dimension columns (type / amount /
  // flight dates). Distinct from `budgetHealth`, which is the derived signal.
  budget: z.object({
    amount: z.number().nullable(),
    currency: z.string().nullable(),
    policy: z.enum(["dateRange", "monthlyRecurring"]).nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
  }),
});
export type PortfolioListItem = z.infer<typeof portfolioListItemSchema>;

/** A member campaign inside a single portfolio's detail view. */
export const portfolioMemberCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z.enum(["enabled", "paused", "archived"]),
  targetingType: z.enum(["manual", "auto"]).nullable(),
  dailyBudget: z.number().nullable(),
  // Amazon campaign flight dates (ISO yyyy-mm-dd); null when the sync has not
  // yet populated them (ADP-121).
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  metrics: metricSnapshotSchema,
});
export type PortfolioMemberCampaign = z.infer<typeof portfolioMemberCampaignSchema>;

export const portfolioAnalyticsQuerySchema = z
  .object({
    range: dashboardRangeSchema.catch("30d"),
    ...reportingDateRangeQueryShape,
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
    // ADP-110: aggregate analytics scope (absent/empty → legacy single-shop).
    shopIds: csvQueryValues(z.string().min(1)),
  })
  .superRefine(validateReportingDateRange);
export type PortfolioAnalyticsQuery = z.infer<typeof portfolioAnalyticsQuerySchema>;

/** `data` shape of `GET /portfolios/analytics`. */
export const portfolioAnalyticsResponseSchema = z.object({
  range: dashboardRangeSchema,
  granularity: campaignAnalyticsGranularitySchema,
  dataThrough: z.string().date().nullable(),
  matchedCampaigns: z.number().int().nonnegative(),
  kpis: z.array(campaignAnalyticsKpiSchema),
  trend: z.array(campaignAnalyticsTrendPointSchema),
  portfolios: z.array(portfolioListItemSchema),
  // ADP-110: see dashboardSummaryResponseSchema.
  currency: z.string().nullable().default(null),
  currencyApproximate: z.boolean().default(false),
  excludedShops: z.number().int().nonnegative().default(0),
});
export type PortfolioAnalyticsResponse = z.infer<typeof portfolioAnalyticsResponseSchema>;

// ── Portfolio creation ──────────────────────────────────────────────────────
//
// The create dialog's contract. `noCap` mirrors Amazon's "no budget" portfolio
// (the budget object is simply omitted from the Amazon write); the other two
// policies map 1:1 onto Amazon's `dateRange` / `monthlyRecurring`.

export const portfolioCreateBudgetSchema = z.discriminatedUnion("policy", [
  z.object({ policy: z.literal("noCap") }),
  z.object({
    policy: z.literal("dateRange"),
    amount: z.number().positive().max(9_999_999.99),
    startDate: z.string().date(),
    endDate: z.string().date().nullable().optional(),
  }),
  z.object({
    policy: z.literal("monthlyRecurring"),
    amount: z.number().positive().max(9_999_999.99),
    endDate: z.string().date().nullable().optional(),
  }),
]);
export type PortfolioCreateBudget = z.infer<typeof portfolioCreateBudgetSchema>;

export const portfolioCreateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    budget: portfolioCreateBudgetSchema,
  })
  .superRefine((input, ctx) => {
    if (
      input.budget.policy === "dateRange" &&
      input.budget.endDate != null &&
      input.budget.endDate < input.budget.startDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budget", "endDate"],
        message: "endDate must be on or after startDate",
      });
    }
  });
export type PortfolioCreateInput = z.infer<typeof portfolioCreateInputSchema>;

/** `data` shape of `POST /portfolios` — the freshly created portfolio. */
export const portfolioCreateResponseDataSchema = z.object({
  id: z.string().uuid(),
  amazonPortfolioId: z.string(),
  name: z.string(),
  state: portfolioStateSchema,
});
export type PortfolioCreateResponseData = z.infer<typeof portfolioCreateResponseDataSchema>;

// ── Portfolio ↔ campaign association (ADP-137) ──────────────────────────────
//
// 「添加广告活动」contract: list the shop's campaigns that belong to no
// portfolio yet, then bulk-attach a selection. Archived campaigns stay listed
// (Xnurta parity); Amazon is the arbiter and rejections come back per campaign.

export const portfolioEligibleCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  campaignType: z.enum(["sp", "sb", "sd"]),
  status: z.enum(["enabled", "paused", "archived"]),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  dailyBudget: z.number().nullable(),
  metrics: metricSnapshotSchema,
});
export type PortfolioEligibleCampaign = z.infer<typeof portfolioEligibleCampaignSchema>;

export const portfolioEligibleCampaignsResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(portfolioEligibleCampaignSchema),
});

export const portfolioAttachCampaignsInputSchema = z.object({
  campaignIds: z.array(z.string().uuid()).min(1).max(50),
});
export type PortfolioAttachCampaignsInput = z.infer<typeof portfolioAttachCampaignsInputSchema>;

export const portfolioAttachFailureSchema = z.object({
  campaignId: z.string().uuid(),
  code: z.string(),
  message: z.string(),
});
export type PortfolioAttachFailure = z.infer<typeof portfolioAttachFailureSchema>;

/** `data` shape of `POST /portfolios/:id/campaigns` — per-campaign outcomes. */
export const portfolioAttachCampaignsResponseDataSchema = z.object({
  attached: z.array(z.string().uuid()),
  failed: z.array(portfolioAttachFailureSchema),
});
export type PortfolioAttachCampaignsResponseData = z.infer<
  typeof portfolioAttachCampaignsResponseDataSchema
>;

/** `data` shape of `GET /portfolios/:id`. */
export const portfolioDetailResponseSchema = z.object({
  // ADP-137: the portfolio's own shop — drives the campaign-creation prefill
  // (`/campaigns/new?shopId=…&portfolioId=…`) from the detail page.
  shopId: z.string().uuid(),
  portfolio: portfolioSummarySchema,
  range: dashboardRangeSchema,
  granularity: campaignAnalyticsGranularitySchema,
  dataThrough: z.string().date().nullable(),
  metrics: metricSnapshotSchema,
  kpis: z.array(campaignAnalyticsKpiSchema),
  trend: z.array(campaignAnalyticsTrendPointSchema),
  memberCampaigns: z.array(portfolioMemberCampaignSchema),
});
export type PortfolioDetailResponse = z.infer<typeof portfolioDetailResponseSchema>;
